import { Button } from '@HoudiniUI/Button';
import { Pill } from '@HoudiniUI/Pill';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { toast } from 'sonner';
import { formatDate, getCategoryLabel, getSourceLabel } from './complianceDerived';
import { styleSheet } from './styles';

const severityPill = (finding: Api.CampaignComplianceFinding): { color: 'red' | 'yellow' | 'gray'; label: string } => {
	if (finding.resolvedAt) {
		return { color: 'gray', label: `Resolved ${formatDate(finding.resolvedAt)}` };
	}
	if (finding.severity === Api.CampaignComplianceSeverity.Blocker) {
		return { color: 'red', label: 'Blocker' };
	}
	if (finding.severity === Api.CampaignComplianceSeverity.Warning) {
		return { color: 'yellow', label: 'Warning' };
	}
	return { color: 'gray', label: finding.severity || 'Unknown' };
};

export const FindingCard = ({ finding }: { finding: Api.CampaignComplianceFinding }) => {
	const pill = severityPill(finding);
	const onCopyFix = async () => {
		try {
			await navigator.clipboard.writeText(finding.suggestedFix);
			toast.success('Suggested fix copied.');
		} catch {
			toast.error('The fix could not be copied to the clipboard.');
		}
	};
	return (
		<article className={css(styleSheet.finding, finding.resolvedAt && styleSheet.findingResolved)}>
			<div className={css(styleSheet.findingTop)}>
				<Pill variant='label' color={pill.color} label={pill.label} />
				<h4 className={css(styleSheet.findingTitle)}>{finding.title ?? finding.code}</h4>
			</div>
			<div className={css(styleSheet.findingMeta)}>
				<span>{finding.code}</span>
				<span>{getCategoryLabel(finding.category)}</span>
				{finding.providerCodes ? (
					<span>
						provider codes{' '}
						{finding.providerHelpUrl ? (
							<a className={css(styleSheet.metaLink)} href={finding.providerHelpUrl} target='_blank' rel='noreferrer'>
								{finding.providerCodes}
							</a>
						) : (
							finding.providerCodes
						)}
					</span>
				) : null}
			</div>
			{finding.evidence?.length ? (
				<ul className={css(styleSheet.evidence)}>
					{finding.evidence.map((evidence, index) => (
						<li key={index} className={css(styleSheet.evidenceItem)}>
							<span className={css(styleSheet.evidenceSource)}>{getSourceLabel(evidence.source)}</span>
							<span>{evidence.detail}</span>
						</li>
					))}
				</ul>
			) : null}
			{finding.suggestedFix ? (
				<div className={css(styleSheet.fix)}>
					<div>
						<span className={css(styleSheet.fixLabel)}>Suggested fix</span>
						{finding.suggestedFix}
					</div>
					<span className={css(styleSheet.fixLinks)}>
						{finding.levitateHelpUrl ? (
							<a
								className={css(styleSheet.fixGuidanceLink)}
								href={finding.levitateHelpUrl}
								target='_blank'
								rel='noreferrer'
							>
								Guidance
							</a>
						) : null}
						<Button variant='outline' size='sm' onClick={onCopyFix}>
							Copy
						</Button>
					</span>
				</div>
			) : null}
		</article>
	);
};
